import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useNavigate } from 'react-router-dom';

interface AIInventoryCardProps {
  totalSystems: number;
  pendingApprovement: number;
  highRiskSystems: number;
  totalDatasets: number;
  sensitiveData: number;
  pendingReview: number;
  isLoadingRecords?: boolean;
}

interface InventoryRowProps {
  label: string;
  value: number;
  highlight?: boolean;
}

const InventoryRow = ({
  label,
  value,
  highlight,
}: InventoryRowProps): React.ReactElement => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    className="inventory-row"
  >
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      className="inventory-row-label-group"
    >
      <Typography className="inventory-row-label">{label}</Typography>
    </Stack>
    <Typography
      className={`inventory-row-value${
        highlight ? ' inventory-row-value--highlight' : ''
      }`}
    >
      {value}
    </Typography>
  </Stack>
);

const AIInventoryCard = ({
  totalSystems,
  pendingApprovement,
  highRiskSystems,
  totalDatasets,
  sensitiveData,
  pendingReview,
  isLoadingRecords = false,
}: AIInventoryCardProps): React.ReactElement => {
  const navigate = useNavigate();
  return (
    <>
      {isLoadingRecords && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}

      {!isLoadingRecords && (
        <Card variant="outlined">
          <CardHeader
            title="AI Inventory"
            subheader="Overview of your AI systems and datasets"
          />
          <CardContent className="inventory-status">
            <Box
              className="inventory-wrapper"
              sx={{
                display: 'grid',
                gap: 2,
                alignItems: 'start',
                gridTemplateColumns: {
                  sx: '1fr',
                  sm: '1fr 1fr',
                  md: '1fr',
                  lg: '1fr',
                },
              }}
            >
              <Card className="inventory-card" variant="outlined">
                <CardHeader
                  className="inventory-card-header"
                  title={
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography className="inventory-card-title">
                        AI Inventory
                      </Typography>
                    </Stack>
                  }
                  action={
                    <IconButton
                      onClick={() => {
                        navigate(`/ai-systems`, {
                          state: { activeTab: '/ai-systems' },
                        });
                      }}
                    >
                      <RemoveRedEyeIcon />
                    </IconButton>
                  }
                />

                <CardContent className="inventory-card-content">
                  <Stack direction="column" gap={0.5}>
                    <InventoryRow
                      label="Total AI Systems"
                      value={totalSystems}
                    />
                    <InventoryRow
                      label="Pending approvals"
                      value={pendingApprovement}
                      highlight
                    />
                    <InventoryRow
                      label="High risk systems"
                      value={highRiskSystems}
                      highlight
                    />
                  </Stack>
                </CardContent>
              </Card>

              <Card className="inventory-card" variant="outlined">
                <CardHeader
                  className="inventory-card-header"
                  title={
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography className="inventory-card-title">
                        Datasets
                      </Typography>
                    </Stack>
                  }
                  action={
                    <IconButton
                      onClick={() => {
                        navigate(`/datasets`);
                      }}
                    >
                      <RemoveRedEyeIcon />
                    </IconButton>
                  }
                />
                <CardContent className="inventory-card-content">
                  <Stack direction="column" gap={0.5}>
                    <InventoryRow
                      label="Total datasets"
                      value={totalDatasets}
                    />
                    <InventoryRow
                      label="Sensitive data"
                      value={sensitiveData}
                    />
                    <InventoryRow
                      label="Pending review"
                      value={pendingReview}
                      highlight
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AIInventoryCard;
